import { useEffect } from 'react';
import { z } from 'zod/v3';
import { getMapBy, encodeStringKey, UUIDType } from '../../../../common/utils';
import { memoize } from '../../../../common/utils/memoize';
import { AlgorithmJoinFormDataSchema } from '../../joins/schema/formData/AlgorithmJoinFormDataSchema';
import { AlgorithmMappingFormDataSchema } from '../../mappings/schema/AlgorithmMappingFormDataSchema';
import { AlgorithmMappingQueryDtoSchema } from '../../mappings/schema/AlgorithmMappingQueryDtoSchema';
import { AlgorithmSourceDimensionFormDataSchema } from '../../sources/schema/AlgorithmSourceFormDataSchema';
import { encodeDimensionCodeValue, getHandleId } from '../utils';
import type {
  AlgorithmJoinsAndMappingsGraphEdgeType,
  AlgorithmJoinsAndMappingsGraphNodeData,
  AlgorithmJoinsAndMappingsGraphNodeOfType,
  AlgorithmJoinsAndMappingsGraphNodeType,
  AlgorithmJoinsAndMappingsGraphNodeTypeName,
} from './types';

export const useAlgorithmJoinsAndMappingsGraphFormDataSchema = () => {
  useEffect(
    () => () => {
      createNodeMemoized.clear();
    },
    [],
  );
  return AlgorithmJoinsAndMappingsGraphFormDataSchema;
};

export const AlgorithmJoinsAndMappingsGraphFormDataSchema = z
  .object({
    joins: z.array(AlgorithmJoinFormDataSchema),
    mappings: z.array(AlgorithmMappingQueryDtoSchema),
    sourceDimensionsMap: z.map(
      z.number(),
      z.map(z.string(), AlgorithmSourceDimensionFormDataSchema),
    ),
  })
  .transform(({ joins, mappings, sourceDimensionsMap }) => {
    const joinsByAlgorithmSourceId = getMapBy(joins, 'algorithmSourceId');
    const mappingsByAlias = getMapBy(mappings, 'alias');

    let nodesById = new Map<UUIDType, AlgorithmJoinsAndMappingsGraphNodeType>();
    const edgesById = new Map<
      UUIDType,
      AlgorithmJoinsAndMappingsGraphEdgeType
    >();

    const addNode = <T extends AlgorithmJoinsAndMappingsGraphNodeTypeName>(
      type: T,
      data: AlgorithmJoinsAndMappingsGraphNodeData<T>,
    ): AlgorithmJoinsAndMappingsGraphNodeOfType<T> => {
      const node = createOrUpdateNode(type, data);
      nodesById.set(node.id as UUIDType, node);
      return node;
    };

    const addEdge = (
      sourceNodeId: UUIDType,
      sourceItemIndex: number,
      targetNodeId: UUIDType,
      targetItemIndex: number,
      data?: { itemId?: number; joinNodeId?: UUIDType },
    ) => {
      const edgeId = window.crypto.randomUUID();
      edgesById.set(edgeId, {
        id: edgeId,
        source: sourceNodeId,
        sourceHandle: getHandleId(sourceNodeId, sourceItemIndex, 'source'),
        target: targetNodeId,
        targetHandle: getHandleId(targetNodeId, targetItemIndex, 'target'),
        ...(data ? { data } : {}),
      });
    };

    joins.forEach((join) => {
      const { id, algorithmSourceId, joinTypeCode } = join;
      const node = addNode('algorithmSource', {
        id,
        algorithmSourceId,
        joinTypeCode,
        items: [],
      });

      let joinNodeItemIndex = 0;
      join.algorithmJoinItems.forEach((item) => {
        if ('algorithmSourceId' in item) {
          const join =
            algorithmSourceId &&
            joinsByAlgorithmSourceId.get(item.algorithmSourceId);

          if (join) {
            const sourceNode = addNode('algorithmSource', {
              id: join.id,
              algorithmSourceId: item.algorithmSourceId,
              joinTypeCode: join.joinTypeCode,
              items: [],
            });
            const sourceItemIndex = sourceNode.data.items.length;

            sourceNode.data.items.push({
              dimensionCode: encodeDimensionCodeValue(
                item.algorithmSourceId,
                item.sourceDimension,
              ),
            });

            if ('algorithmMappingAlias' in item) {
              const algorithmMapping =
                item.algorithmMappingAlias &&
                mappingsByAlias.get(item.algorithmMappingAlias);
              if (algorithmMapping) {
                const mappingNode = addNode('algorithmMapping', {
                  algorithmMapping: AlgorithmMappingFormDataSchema.parse({
                    mapping: algorithmMapping,
                    sourceDimensionsMap,
                  }),
                });
                addEdge(sourceNode.id, sourceItemIndex, mappingNode.id, 0, {
                  itemId: item.id,
                  joinNodeId: node.id,
                });
              }
              return;
            } else {
              node.data.items.push(
                'targetDimension' in item
                  ? {
                      id: item.id,
                      dimensionCode: encodeDimensionCodeValue(
                        algorithmSourceId,
                        item.targetDimension,
                      ),
                    }
                  : { id: item.id, constantValue: item.targetValue },
              );
              addEdge(
                sourceNode.id,
                sourceItemIndex,
                node.id,
                joinNodeItemIndex,
              );
              joinNodeItemIndex++;
            }
          }
        } else if ('algorithmMappingAlias' in item) {
          if (item._variant === 'VALUE_MAPPING') {
            const sourceNode = addNode('constant', {
              constantValue: item.sourceValue,
            });
            const algorithmMapping =
              item.algorithmMappingAlias &&
              mappingsByAlias.get(item.algorithmMappingAlias);
            if (algorithmMapping) {
              const mappingNode = addNode('algorithmMapping', {
                algorithmMapping: AlgorithmMappingFormDataSchema.parse({
                  mapping: algorithmMapping,
                  sourceDimensionsMap,
                }),
              });
              addEdge(sourceNode.id, 0, mappingNode.id, 0, {
                itemId: item.id,
                joinNodeId: node.id,
              });
            }
            return;
          }
          const { algorithmMappingAlias } = item;
          const algorithmMapping =
            algorithmMappingAlias && mappingsByAlias.get(algorithmMappingAlias);

          if (algorithmMapping) {
            const sourceNode = addNode('algorithmMapping', {
              algorithmMapping: AlgorithmMappingFormDataSchema.parse({
                mapping: algorithmMapping,
                sourceDimensionsMap,
              }),
            });

            node.data.items.push(
              'targetDimension' in item
                ? {
                    id: item.id,
                    dimensionCode: encodeDimensionCodeValue(
                      algorithmSourceId,
                      item.targetDimension,
                    ),
                  }
                : { id: item.id, constantValue: item.targetValue },
            );
            addEdge(sourceNode.id, 0, node.id, joinNodeItemIndex);
            joinNodeItemIndex++;
          }
        } else {
          const sourceNode = addNode('constant', {
            constantValue: item.sourceValue,
          });

          node.data.items.push({
            id: item.id,
            dimensionCode: encodeDimensionCodeValue(
              algorithmSourceId,
              item.targetDimension,
            ),
          });
          addEdge(sourceNode.id, 0, node.id, joinNodeItemIndex);
          joinNodeItemIndex++;
        }
      });
    });

    // add nodes for "standalone" mappings (those that are not used in join items) without adding any edges
    mappings.forEach((algorithmMapping) => {
      addNode('algorithmMapping', {
        algorithmMapping: AlgorithmMappingFormDataSchema.parse({
          mapping: algorithmMapping,
          sourceDimensionsMap,
        }),
      });
    });

    return {
      nodes: Array.from(nodesById.values()),
      edges: Array.from(edgesById.values()),
    };
  });

const createOrUpdateNode = <
  T extends AlgorithmJoinsAndMappingsGraphNodeTypeName,
>(
  type: T,
  data: AlgorithmJoinsAndMappingsGraphNodeData<T>,
) => {
  // Create new node object only if it doesn't exist in memo by its unique identifier
  // In case of constant nodes, always create a new one by using random UUID as part of the key
  const nodeMemoKey = encodeStringKey(
    type,
    'algorithmSourceId' in data
      ? data.algorithmSourceId
      : 'algorithmMapping' in data
        ? data.algorithmMapping.alias
        : `${data.constantValue}-${window.crypto.randomUUID()}`,
  );

  return createNodeMemoized(nodeMemoKey, type, data);
};

const createNodeMemoized = memoize(
  <T extends AlgorithmJoinsAndMappingsGraphNodeTypeName>(
    _memoKey: string,
    type: T,
    data: AlgorithmJoinsAndMappingsGraphNodeData<T>,
  ) =>
    ({
      id: window.crypto.randomUUID(),
      type,
      data,
      position: { x: 0, y: 0 },
    }) as AlgorithmJoinsAndMappingsGraphNodeOfType<T>,
);
